import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Box, lighten, Switch, Typography, Unstable_Grid2 as Grid } from '@mui/material';

import {
  ClientGroupSourceFragment,
  ContentApproval,
  GroupSourceType,
  SourceStatus,
  UpdateGroupInput,
  UpdateGroupSourceMutation,
  useUpdateGroupSourceMutation,
} from 'api';
import { useNotify } from 'hooks';
import { useUpdateSource } from 'features/groups/updaters';

import SourceDelete from './SourceDelete';
import { ID_STUB } from 'features/groups/updaters/useCreateSource';
import theme from 'theme';
import { useGroupData } from 'features/groups/hooks';
import { getGroupHexColor } from 'features/groups/helpers';

interface IProps {
  source: ClientGroupSourceFragment;
}

const styles = {
  paper: {
    padding: 2,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
  },
  switch: (hexColor: string) => ({
    '&.MuiSwitch-root .MuiSwitch-switchBase': {
      color: hexColor,
    },
    '&.MuiSwitch-root .MuiSwitch-track': {
      backgroundColor: lighten(hexColor, 0.5),
    },
  }),
} as const;

const Source: FC<IProps> = ({ source }) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { values } = useFormikContext<UpdateGroupInput>();
  const { groupColors } = useGroupData();

  const { id, source_status, source_value, source_type, approval } = source;
  const isActive = source_status === SourceStatus.Active;
  const isCreationInProgress = id == ID_STUB;
  const hexColor = getGroupHexColor(groupColors, values.color);

  const updateSourceMutationOptions = useUpdateSource(values.id);
  const { mutate } = useUpdateGroupSourceMutation({
    onSuccess: handleSuccess,
    ...updateSourceMutationOptions,
  });

  function handleSuccess({ clientGroupSourceUpdate }: UpdateGroupSourceMutation) {
    const { source_status: nextStatus, approval: nextApproval } = clientGroupSourceUpdate;

    let message = '';

    if (nextStatus !== source_status) {
      message = t('groups:sourceStatusUpdate', {
        value: source_value,
        status: nextStatus,
      });
    }

    if (nextApproval !== approval) {
      const isNextAutoApprove = nextApproval === ContentApproval.Autoapproved;
      const statusMessage = isNextAutoApprove ? t('common:enabled') : t('common:disabled');

      message = t('groups:autoApproveUpdate', {
        value: source_value,
        status: statusMessage,
      });
    }

    notify.info({ message });
  }

  function onChangeSwitch() {
    const status = isActive ? SourceStatus.Disabled : SourceStatus.Active;

    mutate({
      data: {
        id,
        source_status: status,
      },
    });
  }

  function renderValue() {
    if (source_type === GroupSourceType.Keyword) {
      return (
        <Typography variant="subtitle2">{`${t('groups:keyword')}: ${source_value}`}</Typography>
      );
    }

    return <Typography variant="subtitle2">{source_value}</Typography>;
  }

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid xs={11}>
        <Box sx={styles.paper}>
          <Grid container alignItems="center">
            <Grid xs>
              {renderValue()}
              <Typography color="textSecondary">
                {approval == 'autoapproved' ? `${t('groups:autoApproveSet')}` : ''}
              </Typography>
            </Grid>
            <Grid>
              <Switch
                color="default"
                checked={isActive}
                onChange={onChangeSwitch}
                disabled={isCreationInProgress}
                sx={styles.switch(hexColor)}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid xs={1}>
        <SourceDelete source={source} />
      </Grid>
    </Grid>
  );
};

export default Source;
